import { useMemo, useState } from 'react';
import type { WorkflowDescriptor, ActionDescriptor } from '@finance/contracts';
import { ActionRenderer } from './ActionRenderer.tsx';
import { useAppI18n } from '../../i18n/use-app-i18n';
import { resolveActions } from '../../runtime/actions/action-resolution';
import { useEvaluationContext } from '../../runtime/conditions/use-evaluation-context';

export interface WorkflowRendererProps {
  contract: WorkflowDescriptor;
  onAction?: (action: ActionDescriptor) => void;
}

export function WorkflowRenderer({ contract, onAction }: WorkflowRendererProps) {
  const { text } = useAppI18n();
  const [currentStepId, setCurrentStepId] = useState(contract.initialStepId);
  const step = contract.steps.find((s) => s.id === currentStepId) ?? contract.steps[0];
  const evalCtx = useEvaluationContext();
  const resolvedActions = useMemo(
    () =>
      step
        ? resolveActions(step.actions, {
            ctx: evalCtx,
          })
        : [],
    [step, evalCtx],
  );

  if (!step) return null;

  const handleAction = (action: ActionDescriptor) => {
    const next = step.nextSteps?.find((n) => !n.condition || action.commandId === n.stepId)?.stepId;
    if (next) setCurrentStepId(next);
    onAction?.(action);
  };

  return (
    <div className="workflow-renderer">
      <h4>{text(step.titleKey)}</h4>
      <div className="workflow-progress">
        {contract.steps.map((s, idx) => (
          <span key={s.id} className={`workflow-step ${s.id === step.id ? 'active' : ''}`}>
            {idx + 1}. {text(s.titleKey)}
          </span>
        ))}
      </div>
      <div className="workflow-actions">
        {resolvedActions.map((action) => (
          <ActionRenderer key={action.action.id} action={action} onAction={(next) => handleAction(next.action)} />
        ))}
      </div>
    </div>
  );
}
